import { parseJsonIfValid } from '@salescore/buff-common'
import { setIsPasting, useAppDispatch } from '@salescore/client-redux'
import type { ViewQueryList } from '@salescore/core'
import { logger } from '@salescore/frontend-common'
import { message } from 'antd'
import { pipe, Result } from 'effect'
import { t } from 'i18next'
import { useCallback } from 'react'

import { useVmid } from '../../../components/VmidContext'
import type { RSheetColumn, RSheetRecordNode } from '../../../domain/column.ts'
import {
  getPasteTargetStructure,
  type PasteTargetStructure,
} from '../../../domain/copyAndPaste/pasteTargetStructure/getPasteTargetStructure.ts'
import { rebuildCopyMatrixForPasteTarget } from '../../../domain/copyAndPaste/pasteTargetStructure/rebuildCopyMatrixForPasteTarget.ts'
import {
  clipboardTextToCopyMatrix,
  type CopyMatrix,
  type CopyMatrixClipboardPayload,
  copyMatrixClipboardWebJsonMimeType,
  isCopyMatrixClipboardPayload,
  normalizeClipboardText,
} from '../../../domain/copyMatrix'
import type { RSheetsCursor } from '../../../domain/cursor/cursorTypes'
import { useCursorMutation, useGetCursorSnapshot } from '../../../recoil/cursor'
import { useRecordsImpl } from '../../../recoil/records'
import type { SalescoreObjectAbility, ViewAbility } from '../../../recoil/view/atoms'
import { usePasteCopyMatrix } from '../../util/usePasteCopyMatrix'

export interface ClipboardReadSuccess {
  success: true
  text: string
  copyMatrixClipboardPayload: CopyMatrixClipboardPayload | undefined
}

interface ClipboardReadFailure {
  success: false
  error: Error
}

type ClipboardReadResult = ClipboardReadSuccess | ClipboardReadFailure

interface UsePasteWillReadClipboardArgument {
  type: 'willReadClipboard'
}

export interface UsePasteAlreadyReadClipboardArgument {
  type: 'alreadyReadClipboard'
  clipboard: ClipboardReadSuccess
}

export type UsePasteArgument = UsePasteWillReadClipboardArgument | UsePasteAlreadyReadClipboardArgument

export const clipboardToCopyMatrix = ({
  text,
  copyMatrixClipboardPayload,
}: Pick<ClipboardReadSuccess, 'text' | 'copyMatrixClipboardPayload'>): CopyMatrix =>
  copyMatrixClipboardPayload?.copyMatrix ?? clipboardTextToCopyMatrix(text)

export const usePaste = (
  columns: Array<RSheetColumn<RSheetRecordNode>>,
  ability: ViewAbility & SalescoreObjectAbility,
  query: ViewQueryList | undefined,
): ((argument: UsePasteArgument) => Promise<void>) => {
  const vmid = useVmid()
  const getCursorSnapshot = useGetCursorSnapshot()
  const records = useRecordsImpl()
  const { hideCopyingArea } = useCursorMutation(ability)
  const dispatch = useAppDispatch()
  const pasteCopyMatrix = usePasteCopyMatrix(columns, ability, query)

  const _paste = useCallback(
    async (cursor: RSheetsCursor, argument: UsePasteAlreadyReadClipboardArgument): Promise<void> => {
      const copyMatrix = clipboardToCopyMatrix(argument.clipboard)
      const updatedCopyMatrix = pipe(
        getPasteTargetStructure({
          copyMatrix,
          cursor,
          columns,
        }),
        Result.map(({ targetColumns, pseudoRootColumn, shouldRebuildCopyMatrix }: PasteTargetStructure) =>
          shouldRebuildCopyMatrix
            ? rebuildCopyMatrixForPasteTarget({
                copyMatrix,
                cursor,
                records,
                targetColumns,
                pseudoRootColumn,
              })
            : copyMatrix,
        ),
      )
      if (Result.isFailure(updatedCopyMatrix)) {
        const reason = updatedCopyMatrix.failure
        switch (reason.kind) {
          case 'CopyMatrixRowsInsufficient': {
            void message.warning(t(`コピーされたデータの行数が不足しています`))
            break
          }
          case 'CopyMatrixWidthZero': {
            void message.warning(t(`コピーされたデータの幅が0です`))
            break
          }
          case 'PasteTargetColumnNotFound': {
            void message.warning(t(`ペースト先の列が見つかりませんでした`))
            break
          }
          case 'PseudoRootColumnNotFound': {
            void message.warning(t(`ペースト先のシート構造に合うコピーされたデータの列が見つかりませんでした`))
            break
          }
        }
        return
      }
      await pasteCopyMatrix(updatedCopyMatrix.success, cursor)
    },
    [columns, pasteCopyMatrix, records],
  )

  return useCallback(
    async (argument: UsePasteArgument): Promise<void> => {
      if (!ability.canSaveRecord && !ability.canEditSalescoreObjectRecord) {
        void message.warning(t(`このシートのレコードを編集する権限がありません`))
        return
      }
      const cursor = getCursorSnapshot()
      if (cursor === undefined) {
        return
      }

      const executePaste = async (pasteArgument: UsePasteAlreadyReadClipboardArgument): Promise<void> => {
        dispatch(setIsPasting({ vmid, value: true }))
        try {
          await _paste(cursor, pasteArgument)
          hideCopyingArea()
        } finally {
          dispatch(setIsPasting({ vmid, value: false }))
        }
      }

      if (argument.type === 'willReadClipboard') {
        const clipboard = await readFromClipboard()
        if (!clipboard.success) {
          return
        }
        await executePaste({
          type: 'alreadyReadClipboard',
          clipboard,
        })
        return
      }
      await executePaste(argument)
    },
    [
      ability.canEditSalescoreObjectRecord,
      ability.canSaveRecord,
      dispatch,
      getCursorSnapshot,
      hideCopyingArea,
      vmid,
      _paste,
    ],
  )
}

async function extractCopyMatrixClipboardPayload(
  items: ClipboardItem[],
): Promise<CopyMatrixClipboardPayload | undefined> {
  const candidates = items.filter((item) => item.types.includes(copyMatrixClipboardWebJsonMimeType))
  const parsedCandidates = await Promise.all(
    candidates.map(async (item) => {
      const blob = await item.getType(copyMatrixClipboardWebJsonMimeType)
      const jsonString = await blob.text()
      return parseJsonIfValid(jsonString)
    }),
  )
  return parsedCandidates.find(isCopyMatrixClipboardPayload)
}

async function extractTextFromClipboardItems(items: ClipboardItem[]): Promise<string | undefined> {
  const textPlainMimeType = 'text/plain'
  const candidate = items.find((item) => item.types.includes(textPlainMimeType))
  if (candidate === undefined) {
    return undefined
  }
  const blob = await candidate.getType(textPlainMimeType)
  return await blob.text()
}

const toClipboardError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(`unknown error in readFromClipboard()`)

export async function readFromClipboard(): Promise<ClipboardReadResult> {
  try {
    const items = await navigator.clipboard.read()
    const copyMatrixClipboardPayload = await extractCopyMatrixClipboardPayload(items)
    const rawTextFromItems = await extractTextFromClipboardItems(items)
    const rawText = rawTextFromItems ?? (await navigator.clipboard.readText())
    return {
      success: true as const,
      text: normalizeClipboardText(rawText),
      copyMatrixClipboardPayload,
    }
  } catch (error) {
    // iframeで使っている際などで、権限が足りず失敗することがある
    // https://sites.google.com/a/chromium.org/dev/Home/chromium-security/deprecating-permissions-in-cross-origin-iframes
    logger.debug(`readFromClipboard() failed: error:`, error)
    return {
      success: false as const,
      error: toClipboardError(error),
    }
  }
}
